import { History, Location } from "history";
import { useRef, useState } from "react";
import { QueryClient, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Dispatch } from "redux";
import { MOBILE_BREAKPOINT } from "../config";
import { AccountsQuery, AccountType } from "../graphql/generated";
import { queryKeys } from "../graphql/queryKeys";
import useAnalytics from "../hooks/useAnalytics";
import { useOutsideAlerter } from "../hooks/useOutsideAlerter";
import { useTheme } from "../hooks/useTheme";
import { useWindowSize } from "../hooks/useWindowSize";
import { authSelectors } from "../store/auth/selector";
import { actions } from "../store/auth/slice";
import styled, { css } from "../styles";
import { darkTheme, lightTheme } from "../styles/theme";
import withMargin from "../styles/withMargin";
import { Avatar } from "./Avatar";
import { Card } from "./Card";
import { Chevron } from "./Chevron";
import { CardDivider } from "./Divider";
import { Flex } from "./Flex";
import { InlineAddBtn } from "./NewButton";
import { Badge } from "./Tab";
import { Tag } from "./Tag";
import { Text } from "./Text";
import { FakeTextLink } from "./TextLink";
import { View } from "./View";

const AvatarWrap = styled(Flex)`
  border-radius: ${(p) => p.theme.misc.borderRadius};
  box-shadow: ${(p) => p.theme.shadow.card};
  color: ${(p) => p.theme.color.typography.secondary};

  &:hover,
  &.isActive {
    box-shadow: ${(p) => p.theme.shadow.cardHeavy};

    svg {
      path {
        fill: ${(p) => p.theme.color.secondary};
      }
    }
  }
`;

interface Props {
  isSignupHeader?: boolean;
}

const switchGroupQueryKeys = [
  queryKeys.myBookings,
  queryKeys.brandLocations,
  queryKeys.brandListings,
  queryKeys.brandBookings,
  queryKeys.reports,
  queryKeys.getBrand,
  queryKeys.billing,
  queryKeys.brandContent,
  queryKeys.accounts,
  queryKeys.brandNotificationCount,
];

// Helper component for rendering brand rows
const BrandRow = ({
  brand: b,
  dispatch,
  track,
  queryClient,
  history,
  location,
  activeBrand,
}: {
  brand: NonNullable<AccountsQuery["account"]["brands"]>[0];
  dispatch: Dispatch<any>;
  track: (event: string, properties?: Record<string, any>) => void;
  queryClient: QueryClient;
  history: History;
  location: Location;
  activeBrand: NonNullable<AccountsQuery["account"]["brands"]>[0] | null;
}) => {
  const notificationCountBookings =
    b.numApplicationsToReview +
    b.numBookingsToAcknowledge +
    b.numBookingsToReview +
    b.numBookingPostSubmissionsToReview +
    b.unreadMessageCountBookings;

  const briefNotificationCount = b.unreadMessageCountBriefs;

  const notificationCount = notificationCountBookings + briefNotificationCount;

  // TODO add content brief media to review

  return (
    <Row
      onClick={() => {
        dispatch(actions.setActiveBrand({ brandId: b.id }));
        dispatch(actions.setActiveGroup({ groupId: "" }));
        track("Switch brand", { brand: b.name });

        switchGroupQueryKeys.forEach((key) => {
          queryClient.resetQueries([key], { exact: false });
        });

        if (location.pathname.startsWith("/i")) {
          history.push("/b");
          return;
        }

        if (location.pathname.startsWith("/b/group-settings")) {
          history.push("/b");
          return;
        }

        if (location.pathname.startsWith("/b/bookings/")) {
          history.push("/b/bookings");
          return;
        }
        if (location.pathname.startsWith("/b/listings/")) {
          history.push("/b/listings");
          return;
        }
        if (location.pathname.startsWith("/b/locations/")) {
          history.push("/b/locations");
          return;
        }
        if (location.pathname.startsWith("/b/brief/")) {
          history.push("/b/briefs");
          return;
        }
        if (location.pathname.startsWith("/b/reports/")) {
          history.push("/b/reports");
          return;
        }
        history.replace(location.pathname);
      }}
    >
      <Flex direction="row" justify="space-between" align="center">
        <Flex align="center">
          <Title>{b.name}</Title>
        </Flex>
        {activeBrand && b.id === activeBrand.id ? (
          <Tag
            shadow={false}
            padding="l"
            size="s"
            color="purple"
            text="Active"
          />
        ) : notificationCount > 0 ? (
          <div style={{ marginLeft: "auto" }}>
            <Badge>{notificationCount}</Badge>
          </div>
        ) : null}
      </Flex>
    </Row>
  );
};

export const AccountSwitcher = ({ isSignupHeader }: Props) => {
  const brand = useSelector(authSelectors.activeBrand);
  const account = useSelector(authSelectors.account);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { track } = useAnalytics();
  const wrapperRef = useRef(null);
  const windowSize = useWindowSize();
  const theme = useTheme();
  const activeGroupId = useSelector(authSelectors.activeGroupId);

  const queryClient = useQueryClient();

  const [showDropdown, setShowDropdown] = useState(false);

  useOutsideAlerter(wrapperRef, () => {
    setShowDropdown(false);
  });

  let avatarUrl;

  if (brand?.logo) {
    avatarUrl = brand.logo;
  } else if (brand?.instagram?.avatar) {
    avatarUrl = brand.instagram.avatar;
  } else if (brand?.tikTok) {
    avatarUrl = brand.tikTok.avatar;
  } else if (
    account?.brandGroups?.find((g) => g.id === activeGroupId) &&
    account.brandGroups.find((g) => g.id === activeGroupId)?.logoUrl
  ) {
    avatarUrl = account.brandGroups.find(
      (g) => g.id === activeGroupId
    )?.logoUrl;
  } else {
    avatarUrl =
      "https://wenibble-images.s3.eu-central-1.amazonaws.com/tiktok_avatars/empty.jpeg";
  }

  const brandNavigation =
    (brand || activeGroupId) &&
    windowSize.width &&
    windowSize.width >= MOBILE_BREAKPOINT &&
    !location.pathname.startsWith("/signup/");

  let sortedBrands =
    account &&
    account.brands &&
    account.brands.slice().sort((a, b) => a.name.localeCompare(b.name));

  let sortedGroups =
    account &&
    account.brandGroups &&
    account.brandGroups.slice().sort((a, b) => a.name.localeCompare(b.name));

  const isStaff = account && account.type === AccountType.AccountTypeStaff;

  if (isStaff) {
    sortedBrands = account?.brands?.slice().sort((a, b) => {
      if (!!a.brandGroup?.id !== !!b.brandGroup?.id) {
        return !!a.brandGroup?.id ? 1 : -1;
      }
      return a.name.localeCompare(b.name);
    });
  }

  const hasApplicationsToReview = sortedBrands
    ? sortedBrands
        .filter((b) => b.id !== brand?.id)
        .some(
          (b) =>
            b.numApplicationsToReview > 0 ||
            b.numBookingsToAcknowledge > 0 ||
            b.numBookingsToReview > 0 ||
            b.numBookingPostSubmissionsToReview > 0
        )
    : false;

  return (
    <SwitcherWrap
      brandNavigation={brandNavigation ? true : false}
      ref={wrapperRef}
      onClick={() => setShowDropdown((s) => !s)}
    >
      {brandNavigation ? (
        <AvatarWrap justify="space-between" align="center">
          <Flex
            margin="s m"
            style={{ gap: lightTheme.spacing.m }}
            align="center"
          >
            <Avatar
              withShadow={false}
              borderSize={0}
              size={32}
              url={avatarUrl}
            />
            {hasApplicationsToReview ? (
              <AbsoluteWrap className="account-switcher-indicator">
                <NotificationIndicator isActive={!showDropdown} />
              </AbsoluteWrap>
            ) : null}

            <div className="account-switcher">
              <Flex direction="column" margin="0 xs 0 0">
                <BrandName truncate>
                  {activeGroupId
                    ? account?.brandGroups?.find((g) => g.id === activeGroupId)
                        ?.name
                    : brand?.name}
                </BrandName>
              </Flex>
            </div>
          </Flex>
          {/* <AccountSwitcherIcon colorPreset="secondary" /> */}
          <div className="account-switcher">
            <View margin="0 m 0 0">
              <Chevron
                animateTransition
                size="s"
                direction={showDropdown ? undefined : "down"}
              />
            </View>
          </div>
        </AvatarWrap>
      ) : (
        <Avatar
          url={
            avatarUrl
              ? avatarUrl
              : "https://wenibble-images.s3.eu-central-1.amazonaws.com/tiktok_avatars/empty.jpeg"
          }
        />
      )}

      {showDropdown ? (
        <DropdownWrap brandNavigation={brandNavigation ? true : false}>
          <Dropdown
            themeName={theme.name as "light" | "dark"}
            shadow="heavy"
            brandNavigation={brandNavigation ? true : false}
            padding="s 0"
            isSignupHeader={isSignupHeader}
          >
            {sortedBrands && sortedBrands.length > 0 ? (
              <>
                {(() => {
                  // Only show group organization if user has group-level access
                  const userHasGroupAccess =
                    sortedGroups && sortedGroups.length > 0;

                  if (userHasGroupAccess) {
                    // User has group access - only show groups they have access to with headers
                    const userGroupIds = new Set(sortedGroups.map((g) => g.id));

                    // Separate brands into those from accessible groups vs others
                    const brandsFromAccessibleGroups = sortedBrands.filter(
                      (b) =>
                        b.brandGroup?.id && userGroupIds.has(b.brandGroup.id)
                    );
                    const otherBrands = sortedBrands.filter(
                      (b) =>
                        !b.brandGroup?.id || !userGroupIds.has(b.brandGroup.id)
                    );

                    // Create a map of accessible group ID to brands
                    const brandsByGroup = new Map<
                      string,
                      typeof sortedBrands
                    >();
                    brandsFromAccessibleGroups.forEach((brand) => {
                      const groupId = brand.brandGroup!.id;
                      if (!brandsByGroup.has(groupId)) {
                        brandsByGroup.set(groupId, []);
                      }
                      brandsByGroup.get(groupId)!.push(brand);
                    });

                    // Sort brands within each group
                    brandsByGroup.forEach((brands) => {
                      brands.sort((a, b) => a.name.localeCompare(b.name));
                    });

                    return (
                      <>
                        {/* Render groups user has access to */}
                        {sortedGroups.map((group, groupIndex) => {
                          const groupBrands = brandsByGroup.get(group.id) || [];

                          // Only render group if it has brands
                          if (groupBrands.length === 0) return null;

                          return (
                            <div key={`group-${group.id}`}>
                              {groupIndex > 0 && <CardDivider margin="s 0" />}
                              <Flex
                                justify="space-between"
                                align="center"
                                margin="s l xxs"
                              >
                                <GroupHeader>{group.name}</GroupHeader>
                                <FakeTextLink
                                  margin="0"
                                  size="xxs"
                                  onClick={() => {
                                    dispatch(
                                      actions.setActiveGroup({
                                        groupId: group.id,
                                      })
                                    );
                                    dispatch(
                                      actions.setActiveBrand({ brandId: "" })
                                    );
                                    track("Switch group", {
                                      group: group.name,
                                    });

                                    switchGroupQueryKeys.forEach((key) => {
                                      queryClient.resetQueries([key], {
                                        exact: false,
                                      });
                                    });

                                    history.push("/b");
                                  }}
                                >
                                  View Group
                                </FakeTextLink>
                              </Flex>

                              {groupBrands.map((b, i) => (
                                <BrandRow
                                  key={`${group.id}-brand-${i}`}
                                  brand={b}
                                  dispatch={dispatch}
                                  track={track}
                                  queryClient={queryClient}
                                  history={history}
                                  location={location}
                                  activeBrand={brand}
                                />
                              ))}
                            </div>
                          );
                        })}

                        {/* Add divider before other brands if there are accessible groups with brands */}
                        {otherBrands.length > 0 &&
                          brandsFromAccessibleGroups.length > 0 && (
                            <CardDivider margin="s 0" />
                          )}

                        {/* Render all other brands (ungrouped + brands from inaccessible groups) */}
                        {otherBrands.map((b, i) => (
                          <BrandRow
                            key={`other-${i}`}
                            brand={b}
                            dispatch={dispatch}
                            track={track}
                            queryClient={queryClient}
                            history={history}
                            location={location}
                            activeBrand={brand}
                          />
                        ))}
                      </>
                    );
                  }

                  // User has no group access - show simple flat list of all brands
                  return (
                    <>
                      {sortedBrands.map((b, i) => (
                        <BrandRow
                          key={`brand-${i}`}
                          brand={b}
                          dispatch={dispatch}
                          track={track}
                          queryClient={queryClient}
                          history={history}
                          location={location}
                          activeBrand={brand}
                        />
                      ))}
                    </>
                  );
                })()}
                <Row onClick={() => history.push("/signup/b/create-brand")}>
                  <InlineAddBtn label="Add brand" margin="0" />
                </Row>
              </>
            ) : (
              <Text margin="xs l" colorPreset="secondary">
                No brands created yet
              </Text>
            )}
          </Dropdown>
        </DropdownWrap>
      ) : null}
    </SwitcherWrap>
  );
};

const AbsoluteWrap = styled.div`
  position: absolute;
  right: 4px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99;
  transition: right 150ms ease-out;
`;

const NotificationIndicator = styled.span<{ isActive: boolean }>`
  height: ${(p) => p.theme.spacing.xs};
  width: ${(p) => p.theme.spacing.xs};
  border-radius: 100%;
  background-color: ${(p) => p.theme.color.primary};
  opacity: ${(p) => (p.isActive ? 1 : 0)};
  transition: opacity 150ms ease-out;
`;

// To fix the issue of the account switcher being visible on mobile overlays use excluded URLs in BrandNavigation.tsx
const SwitcherWrap = styled.div<{ brandNavigation: boolean }>`
  position: relative;
  user-select: none;
  z-index: 99;
  ${(p) =>
    p.brandNavigation &&
    css`
      width: 100%;
      cursor: pointer;
    `}
`;

const DropdownWrap = styled.div<{ brandNavigation: boolean }>`
  position: absolute;
  right: 0;
  z-index: 99;
  width: 320px;

  ${(p) =>
    p.brandNavigation
      ? css`
          @media (min-width: ${MOBILE_BREAKPOINT}px) {
            left: 100%;
            top: -${(p) => p.theme.spacing.m};
            padding-left: ${(p) => p.theme.spacing.l};
          }
        `
      : null}
`;

interface DropdownProps {
  brandNavigation: boolean;
  themeName: "light" | "dark";
  isSignupHeader?: boolean;
}

const Dropdown = styled(Card)<DropdownProps>`
  z-index: 99;
  overflow-y: scroll;
  max-height: 90vh;
  margin-top: ${(p) => p.theme.spacing.m};
  cursor: default;

  ${(p: DropdownProps) =>
    p.brandNavigation &&
    !p.isSignupHeader &&
    css`
      background: ${(p: DropdownProps) =>
        p.themeName === "light"
          ? lightTheme.color.background.behind
          : darkTheme.color.background.behind} !important;
    `}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    background: ${(p) => p.theme.color.card.background} !important;
  }

  ${(p) =>
    p.brandNavigation
      ? css`
          @media (min-width: ${MOBILE_BREAKPOINT}px) {
            left: 100%;
            /* top: ${(p) => p.theme.spacing.xxl}; */
            top: -${(p) => p.theme.spacing.m};
          }
          box-shadow: ${(p) => p.theme.shadow.cardHeavy};
        `
      : null}
`;

const Row = styled.div<{ indented?: boolean }>`
  padding: calc(${(p) => p.theme.spacing.s} + 2px) ${(p) => p.theme.spacing.m};
  color: ${(p) => p.theme.color.typography.secondary};
  margin: 0 ${(p) => p.theme.spacing.s} ${(p) => p.theme.spacing.xs};
  border-radius: ${(p) => p.theme.misc.borderRadius};

  &:last-of-type {
    margin-bottom: 0;
  }

  &:hover,
  &.isActive {
    background-color: ${(p) => p.theme.color.input.hover};

    p {
      color: ${(p) => p.theme.color.primary};
    }

    svg {
      path {
        stroke: ${(p) => p.theme.color.primary};
      }
    }
  }

  cursor: pointer;
`;

const Title = styled.p`
  font-family: ${(p) => p.theme.typography.bodyFamily};
  color: ${(p) => p.theme.color.typography.heading};
  font-size: ${(p) => p.theme.typography.size.m};
  line-height: 1.3em;
  font-weight: ${(p) => p.theme.typography.weight.semi};
  margin: 0;
`;

const BrandName = styled(Text)<{ margin?: string }>`
  font-family: ${(p) => p.theme.typography.bodyFamily};
  font-size: ${(p) => p.theme.typography.size.s};
  color: ${(p) => p.theme.color.typography.secondary};
  line-height: 1.3em;
  margin: 0;
  ${withMargin};
`;

const GroupHeader = styled.div`
  font-family: ${(p) => p.theme.typography.bodyFamily};
  color: ${(p) => p.theme.color.typography.secondary};
  font-size: ${(p) => p.theme.typography.size.xxs};
  font-weight: ${(p) => p.theme.typography.weight.semi};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;
